import { useMutation, useQuery, useQueryClient } from 'react-query';
import { apiGet, apiPut } from '../services/apiClient';

/**
 * =============================================================================
 * `/api/SiteSettings/Features` — the public site's feature switches
 * =============================================================================
 *
 * Spec sent to the backend team:
 * DPA-FE `docs/APICall/SPEC-site-feature-toggles.md`.
 *
 * ⚠ NOT DEPLOYED. Probed 2026-11-03 against dpaapis.ctsdemo.ae:
 *
 *   GET /api/SiteSettings/Features   404   (bare, no envelope)
 *   GET /api/Auth/GetPageAccess      200   (control: the host is up)
 *
 * The call below is written to the contract ANYWAY, which is the house rule
 * (api-contract skill §8): a conforming request against a missing route gives
 * a visible 404, and the network tab then shows unambiguously that the server
 * is the thing that is missing. No client-side shim, no legacy fallback.
 *
 * The screen degrades to "not deployed yet, here is what it will control"
 * rather than erroring, because an operator who opens it today should learn
 * what is coming, not meet a red toast.
 *
 * -----------------------------------------------------------------------------
 * THIS IS A LIST, NOT A PAGINATED LIST.
 *
 * There are a dozen switches and there will never be a thousand, so it goes
 * through `apiGet` and not `apiGetList` — the latter appends `/{page}/{pageSize}`
 * to the path and would request `/Features/1/0`, which is a different route.
 *
 * -----------------------------------------------------------------------------
 * WHY THE SAVE IS A BATCH.
 *
 * These switches change what a LIVE PUBLIC SITE shows. An operator reviewing
 * "I am about to hide the media centre and turn the chatbot on" before
 * committing is worth more than the small convenience of instant toggles, and
 * a batch makes that review one atomic action instead of five requests that
 * can half-apply. The contract's batch rule is explicit: process every item
 * and report partial failure rather than silently dropping any.
 */

const KEY = ['siteFeatures'];

/**
 * Every switch and its current state.
 *
 * Resolves to `{ items }` with `items` always an array, so a caller never has
 * to guard — and a 404 from the undeployed route becomes an empty list, which
 * the screen reads as "not configured yet" and renders from the catalogue's
 * defaults. That is the same state the WEBSITE will be in, so the two agree.
 */
export const useSiteFeatures = () =>
  useQuery(
    KEY,
    async () => {
      const data = await apiGet('SiteSettings/Features');
      return {
        items: Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
            ? data
            : [],
      };
    },
    {
      // A 404 is a missing route; retrying it three times only delays the
      // message by a second and a half and triples the noise.
      retry: (failureCount, error) =>
        error?.status !== 404 && failureCount < 1,
      staleTime: 30 * 1000,
      placeholderData: { items: [] },
    }
  );

/**
 * Save the switches that changed.
 *
 * Takes `[{ key, isEnabled }]` — ONLY the changes, not the whole set. Sending
 * every switch on every save would make one operator's stale screen silently
 * revert another operator's change made a minute earlier; sending only the
 * deltas means two people editing different switches do not fight.
 *
 * The answer is expected to report per-item results (contract §3, batch). A
 * partial failure must reach the operator naming which switch did not apply —
 * `GeneralSettings` renders that rather than a generic toast, because "some of
 * it worked" is the one outcome a success message must never cover.
 */
export const useSaveSiteFeatures = () => {
  const qc = useQueryClient();
  return useMutation(
    (changes) =>
      apiPut('SiteSettings/Features', {
        items: (Array.isArray(changes) ? changes : []).map(
          ({ key, isEnabled }) => ({ key, isEnabled: Boolean(isEnabled) })
        ),
      }),
    { onSuccess: () => qc.invalidateQueries(KEY) }
  );
};

/**
 * Toggle one switch on its own.
 *
 * Not used by the settings screen, which batches. Here because the contract
 * asks for the single-item route anyway and a future "turn this off now"
 * control — from an incident, say — should not have to send a batch of one.
 */
export const useToggleSiteFeature = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ key, isEnabled }) =>
      apiPut(`SiteSettings/Features/${encodeURIComponent(key)}`, {
        isEnabled: Boolean(isEnabled),
      }),
    { onSuccess: () => qc.invalidateQueries(KEY) }
  );
};

const siteFeaturesApi = {
  useSiteFeatures,
  useSaveSiteFeatures,
  useToggleSiteFeature,
};

export default siteFeaturesApi;
